import { classes } from "polytype";
import { AppInstallation } from "../../app";
import {
  Bytes,
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base";
import assertObjectFound from "../../base/assertObjectFound.js";
import { config } from "../../config/config.js";
import type { Project } from "../../project";
import { type AsyncResourceVariant, provideReact } from "../../react";
import type { DatabaseType } from "../types.js";
import type {
  RedisConfiguration,
  RedisConfigurationUpdateRequestData,
  RedisCreateRequestData,
  RedisData,
  RedisListItemData,
  RedisListQueryData,
  RedisMaxMemoryPolicy,
} from "./types.js";

export class Redis extends ReferenceModel {
  public static ofId(id: string) {
    return new Redis(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.redis.find(id);
    if (data !== undefined) {
      return new RedisDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const database = await this.find(id);
    assertObjectFound(database, "database", id);
    return database;
  });

  public findDetailed = provideReact(
    () => Redis.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<RedisDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Redis.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<RedisDetailed>>;

  public static query(project: Project, query: RedisListQueryData = {}) {
    return new RedisListQuery(project, query);
  }

  public static async create(project: Project, data: RedisCreateRequestData) {
    const { id } = await config.behaviors.redis.create(project.id, data);
    return new Redis(id);
  }

  public static listVersions = provideReact(async (project: Project) => {
    const versions = await config.behaviors.redis.listVersions(project.id);

    return versions
      .filter((v) => !v.disabled)
      .sort((a, b) => parseFloat(b.number) - parseFloat(a.number));
  });

  public static getLatestVersion = provideReact(async (project: Project) => {
    const versions = await Redis.listVersions(project);
    return versions[0];
  });

  public async delete() {
    await config.behaviors.redis.delete(this.id);
  }

  public async updateDescription(description: string) {
    await config.behaviors.redis.updateDescription(this.id, description);
  }

  public async updateConfiguration(data: RedisConfigurationUpdateRequestData) {
    await config.behaviors.redis.updateConfiguration(this.id, data);
  }
}

class RedisCommon extends classes(
  DataModel<RedisListItemData | RedisData>,
  Redis,
) {
  public readonly name: string;
  public readonly description: string;
  public readonly version: string;
  public readonly type: DatabaseType;
  public readonly storageUsage: Bytes;
  public readonly configuration?: RedisConfiguration;
  public readonly hostname: string;
  public readonly port: number;
  public readonly connectionString: string;
  public readonly linkedAppInstallations: AppInstallation[];

  public constructor(data: RedisListItemData | RedisData) {
    super([data], [data.id]);
    this.name = data.name;
    this.description = data.description;
    this.version = data.version;
    this.type = "Redis";
    this.storageUsage = Bytes.of(data.storageUsageInBytes, "bytes");
    if (data.configuration) {
      this.configuration = {
        maxMemoryPolicy: data.configuration.maxMemoryPolicy as
          | RedisMaxMemoryPolicy
          | undefined,
        maxMemory: data.configuration.maxMemory
          ? Bytes.of(parseInt(data.configuration.maxMemory), "bytes")
          : undefined,
        persistent: !!data.configuration.persistent,
      };
    }
    this.hostname = data.hostname;
    this.port = data.port;
    this.connectionString = `redis://${data.hostname}:${data.port}`;

    this.linkedAppInstallations =
      data.finalizers?.map((f) =>
        AppInstallation.ofId(f.split(":")?.[2] ?? ""),
      ) ?? [];
  }
}

export class RedisDetailed extends classes(RedisCommon, DataModel<RedisData>) {
  public constructor(data: RedisData) {
    super([data], [data]);
  }
}

export class RedisListItem extends classes(
  RedisCommon,
  DataModel<RedisListItemData>,
) {
  public constructor(data: RedisListItemData) {
    super([data], [data]);
  }
}

export class RedisListQuery extends ListQueryModel<RedisListQueryData> {
  public readonly project: Project;

  public constructor(project: Project, query: RedisListQueryData = {}) {
    super(query, {
      dependencies: [project.id],
    });
    this.project = project;
  }

  public refine(query: RedisListQueryData) {
    return new RedisListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.redis.list(
      this.project.id,
      this.query,
    );
    return new RedisList(
      this.project,
      this.query,
      items
        .map((d) => new RedisListItem(d))
        .sort((a, b) => a.description.localeCompare(b.description)),
    );
  }, [this.queryId, "redis"]);
}

export class RedisList extends classes(
  RedisListQuery,
  ListDataModel<RedisListItem>,
) {
  public constructor(
    project: Project,
    query: RedisListQueryData,
    databases: RedisListItem[],
  ) {
    super([project, query], [databases, databases.length]);
  }
}
